import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box, Typography, Card, Skeleton, Button, Chip, Grid, Stack, Divider,
  TextField, InputAdornment, Snackbar, Alert, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import GradeOutlinedIcon from "@mui/icons-material/GradeOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { useGetMyAssessmentDetailQuery, useUpdateMyAssessmentMutation } from "../../Api/attoDeskApi";
import useUser from "../../Core/hook/useUser";

// Palette shared with the Staff Portal assessments list + admin
// assessments page — keeps both surfaces in the same visual family.
const NAVY = "#1b3a6b";
const NAVY_2 = "#2a5298";
const PAGE_BG = "#f0f4f8";

type Detail = {
  assessment_id: number;
  title: string;
  subject: string | null;
  grade: string | null;
  assessment_date: string | null;
  score: number | null;
  max_score: number | null;
  percentage: number | null;
  marked: boolean;
  result_status: string | null;
  pass_fail: boolean | null;
  pass_threshold: number | null;
  student: { id: number; name: string; grade: string | null } | null;
  focus: string | null;
  analysis: string | null;
  comment_1: string | null;
  comment_2: string | null;
  comment_3: string | null;
  type: string | null;
  due_date: string | null;
  instructions: string | null;
  graded_at: string | null;
  updated_at: string | null;
  graded_by: number | null;
  graded_by_name: string | null;
  // Materials attached at assessment-creation time. Live on
  // assessment_attachments (linked to assessment_group_id) — shared by
  // every student assessment under the same header. Empty array when
  // nothing was attached.
  learning_materials?: Array<
    | {
        id: number;
        kind: "file";
        name: string;
        url: string;
        mime_type: string | null;
        file_size: number | null;
        extension: string | null;
      }
    | {
        id: number;
        kind: "youtube";
        url: string;
        youtube_video_id: string | null;
      }
  >;
};

const humanFileSize = (bytes: number | null): string => {
  if (bytes == null || !Number.isFinite(bytes) || bytes <= 0) return "";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

const fileKindLabel = (ext: string | null, mime: string | null): string => {
  const e = (ext || "").toLowerCase();
  if (e === "pdf") return "PDF Document";
  if (["doc", "docx"].includes(e)) return "Word Document";
  if (["ppt", "pptx"].includes(e)) return "Presentation";
  if (["xls", "xlsx"].includes(e)) return "Spreadsheet";
  if (["jpg", "jpeg", "png", "webp", "gif"].includes(e)) return "Image";
  if (e === "txt" || (mime || "").startsWith("text/")) return "Text File";
  if (e) return e.toUpperCase() + " File";
  return "Document";
};

const fileIcon = (ext: string | null, mime: string | null) => {
  const e = (ext || "").toLowerCase();
  if (e === "pdf") return <PictureAsPdfOutlinedIcon sx={{ fontSize: 22, color: "#dc2626" }} />;
  if (["jpg", "jpeg", "png", "webp", "gif"].includes(e) || (mime || "").startsWith("image/")) {
    return <ImageOutlinedIcon sx={{ fontSize: 22, color: "#0891b2" }} />;
  }
  if (["doc", "docx"].includes(e)) return <DescriptionOutlinedIcon sx={{ fontSize: 22, color: "#2563eb" }} />;
  return <InsertDriveFileOutlinedIcon sx={{ fontSize: 22, color: NAVY }} />;
};

const fmtDate = (iso: string | null) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return "—"; }
};
const fmtDateTime = (iso: string | null) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })} · ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
  } catch { return "—"; }
};

// Human labels for the closed-vocab enums stored on the backend.
// Never render the raw snake_case value in the UI.
const humanizeType = (t: string | null): string => {
  if (!t) return "—";
  return t.charAt(0).toUpperCase() + t.slice(1);
};
const workflowStatus = (d: Detail): { label: string; bg: string; fg: string; border: string } => {
  if (d.marked || d.result_status === "graded") return { label: "Graded", bg: "#dcfce7", fg: "#166534", border: "#bbf7d0" };
  const isUpcoming = d.assessment_date && new Date(d.assessment_date).getTime() > Date.now();
  if (isUpcoming) return { label: "Upcoming", bg: "#f3e8ff", fg: "#6b21a8", border: "#e9d5ff" };
  return { label: "Needs Review", bg: "#fef3c7", fg: "#92400e", border: "#fde68a" };
};
const resultDisplay = (d: Detail): string => {
  if (!d.marked || d.percentage === null) return "Not Graded";
  const pct = `${Math.round(d.percentage)}%`;
  if (d.pass_threshold === null || d.pass_fail === null) return pct;
  return `${pct} · ${d.pass_fail ? "Passed" : "Below Pass Mark"}`;
};

const KpiTile: React.FC<{ label: string; value: React.ReactNode; accent: string; icon: React.ReactNode; bg: string }> = ({ label, value, accent, icon, bg }) => (
  <Box sx={{
    p: 2, borderRadius: 2, border: "1px solid #e2e8f0", background: "#fff",
    position: "relative", overflow: "hidden", height: "100%",
    "&::before": { content: '""', position: "absolute", left: 0, top: 0, bottom: 0, width: 3, bgcolor: accent },
  }}>
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.5 }}>
      <Box sx={{ width: 30, height: 30, borderRadius: "8px", bgcolor: bg, color: accent, display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 18 } }}>
        {icon}
      </Box>
      <Typography sx={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748b" }}>
        {label}
      </Typography>
    </Stack>
    <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a", lineHeight: 1.25 }}>
      {value}
    </Typography>
  </Box>
);

const LabelValue: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box>
    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748b", mb: 0.5 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>
      {value ?? <Box component="span" sx={{ color: "#94a3b8", fontStyle: "italic", fontWeight: 500 }}>Not provided</Box>}
    </Typography>
  </Box>
);

const TextSection: React.FC<{ title: string; body: string | null }> = ({ title, body }) => (
  <Card sx={{ p: 2.5, borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(15,23,42,0.04)" }}>
    <Typography sx={{ fontSize: 13, fontWeight: 800, color: NAVY, textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>{title}</Typography>
    {body && body.trim().length > 0 ? (
      <Typography sx={{ fontSize: 13.5, color: "#1e293b", whiteSpace: "pre-wrap", lineHeight: 1.55 }}>{body}</Typography>
    ) : (
      <Typography sx={{ fontSize: 13, color: "#94a3b8", fontStyle: "italic" }}>Not provided</Typography>
    )}
  </Card>
);

// Quick-feedback chips that append editable text to the "Overall Feedback"
// textarea. Frontend-only — no template storage exists in the model.
// ── Local draft persistence for the Grade & Feedback editor ─────────
// Namespaced by BOTH the acting staff/user scope AND the assessment id
// so drafts for different teachers/assessments never collide. Stored
// separately from the auth token — only the editable grading fields
// live in localStorage; no student PII, no permissions, no API tokens.
const DRAFT_KEY_PREFIX = "staff-assessment-grade-draft";
type DraftForm = {
  student_mark: string;
  comment_1: string;
  comment_2: string;
  comment_3: string;
  analysis: string;
};
type DraftEnvelope = {
  assessmentId: number;
  userScope: string;
  form: DraftForm;
  serverUpdatedAt: string | null;
  updatedAt: string;
};
const draftKey = (userScope: string, assessmentId: number) =>
  `${DRAFT_KEY_PREFIX}:${userScope}:${assessmentId}`;
const readDraft = (userScope: string, assessmentId: number): DraftEnvelope | null => {
  try {
    const raw = window.localStorage.getItem(draftKey(userScope, assessmentId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DraftEnvelope;
    if (!parsed || parsed.assessmentId !== assessmentId || parsed.userScope !== userScope) return null;
    return parsed;
  } catch {
    return null;
  }
};
const writeDraft = (env: DraftEnvelope): void => {
  try { window.localStorage.setItem(draftKey(env.userScope, env.assessmentId), JSON.stringify(env)); }
  catch { /* quota / private mode — fail silently, form state remains in memory */ }
};
const clearDraft = (userScope: string, assessmentId: number): void => {
  try { window.localStorage.removeItem(draftKey(userScope, assessmentId)); }
  catch { /* noop */ }
};
const shallowFormEq = (a: DraftForm, b: DraftForm): boolean =>
  a.student_mark === b.student_mark &&
  a.comment_1 === b.comment_1 &&
  a.comment_2 === b.comment_2 &&
  a.comment_3 === b.comment_3 &&
  a.analysis === b.analysis;

const FEEDBACK_CHIPS = [
  "Great progress",
  "Good understanding",
  "Needs more practice",
  "Review this concept",
  "Show your work",
  "Excellent effort",
];

const AssessmentDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  const assessmentId = Number(id);
  const { data, isLoading, isFetching, isError, error, refetch } = useGetMyAssessmentDetailQuery(
    { assessmentId: assessmentId || 0 },
    { skip: !assessmentId || Number.isNaN(assessmentId) },
  );
  const [updateAssessment, { isLoading: isSaving }] = useUpdateMyAssessmentMutation();

  // Prefer the last successful detail payload so a transient background
  // refetch error (e.g. brief 500 after invalidation) can NEVER replace
  // a valid, already-rendered assessment with the generic
  // "We couldn't load this assessment" error state. Also lets us seed
  // the page directly from a successful mutation response.
  const freshFromServer = (data && (data as any).status && (data as any).data) as Detail | undefined;
  const [lastGoodDetail, setLastGoodDetail] = useState<Detail | undefined>(undefined);
  useEffect(() => { if (freshFromServer) setLastGoodDetail(freshFromServer); }, [freshFromServer]);
  const detail = freshFromServer ?? lastGoodDetail;
  const httpStatus = (error as any)?.status;

  // Edit-mode toggle. Auto-opens when the URL carries ?mode=grade (green
  // icon on the list), otherwise stays in read mode. The grading panel
  // is on the same page — never navigate to a second URL for grading.
  const [editing, setEditing] = useState(false);
  const gradingPanelRef = useRef<HTMLDivElement | null>(null);

  const [form, setForm] = useState<DraftForm>({
    student_mark: "",
    comment_1: "",
    comment_2: "",
    comment_3: "",
    analysis: "",
  });
  const [scoreError, setScoreError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ severity: "success" | "error"; message: string } | null>(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [staleDraft, setStaleDraft] = useState<{ draftServerAt: string | null; currentServerAt: string | null } | null>(null);

  // Draft-scope key uses staffId when available, otherwise a short
  // fallback tag. NEVER put an auth token here — this string is only a
  // namespace and appears in localStorage keys only.
  const { staffId, userData } = useUser();
  const userScope = useMemo(() => {
    if (staffId != null) return `s${staffId}`;
    return userData?.username ? `u${userData.username}` : "anon";
  }, [staffId, userData?.username]);

  // Build the reference form derived from the current server payload.
  // Score-0 handled with nullish so "0" never collapses to "" (the
  // classic `x || ""` bug on legitimate zero grades).
  const serverForm = useMemo<DraftForm | null>(() => {
    if (!detail) return null;
    return {
      student_mark: detail.marked && detail.score !== null && detail.score !== undefined ? String(detail.score) : "",
      comment_1: detail.comment_1 ?? "",
      comment_2: detail.comment_2 ?? "",
      comment_3: detail.comment_3 ?? "",
      analysis: detail.analysis ?? "",
    };
  }, [detail?.assessment_id, detail?.marked, detail?.score, detail?.comment_1, detail?.comment_2, detail?.comment_3, detail?.analysis, detail?.updated_at]);

  // Hydration is one-shot per (userScope, assessmentId). Priority:
  //   1. Non-stale local draft
  //   2. Server-saved values
  //   3. Empty defaults
  // Guarded by a ref so a later RTK refetch (e.g. window-focus)
  // NEVER clobbers the teacher's live typing.
  const hydratedRef = useRef<{ userScope: string; assessmentId: number } | null>(null);
  useEffect(() => {
    if (!detail || !serverForm) return;
    const already = hydratedRef.current;
    if (already && already.userScope === userScope && already.assessmentId === detail.assessment_id) return;
    const stored = readDraft(userScope, detail.assessment_id);
    const currentServerAt = detail.updated_at ?? null;
    if (stored) {
      // Stale check: server updated_at moved after the draft was made.
      // We keep the draft in storage (never silently discard the
      // teacher's work) but flag it so the UI can offer both choices.
      const isStale = !!(stored.serverUpdatedAt && currentServerAt && stored.serverUpdatedAt !== currentServerAt);
      if (isStale) {
        setStaleDraft({ draftServerAt: stored.serverUpdatedAt, currentServerAt });
        // Default to server; user can restore the draft.
        setForm(serverForm);
      } else {
        setForm(stored.form);
        setDraftSaved(true);
      }
    } else {
      setForm(serverForm);
    }
    hydratedRef.current = { userScope, assessmentId: detail.assessment_id };
  }, [detail?.assessment_id, userScope, serverForm]);

  // Debounced write on any form change (only after hydration for this
  // assessment). Skipping the pre-hydration window prevents an empty
  // default overwriting a valid stored draft during first mount.
  useEffect(() => {
    const hyd = hydratedRef.current;
    if (!hyd || !detail || hyd.assessmentId !== detail.assessment_id) return;
    // Don't persist when the form matches the server payload — that's
    // just the read-mode snapshot, no draft is required.
    if (serverForm && shallowFormEq(form, serverForm)) {
      setDraftSaved(false);
      clearDraft(userScope, detail.assessment_id);
      return;
    }
    const t = window.setTimeout(() => {
      writeDraft({
        assessmentId: detail.assessment_id,
        userScope,
        form,
        serverUpdatedAt: detail.updated_at ?? null,
        updatedAt: new Date().toISOString(),
      });
      setDraftSaved(true);
    }, 400);
    return () => window.clearTimeout(t);
  }, [form, detail?.assessment_id, detail?.updated_at, userScope, serverForm]);

  const isDirty = useMemo(() => (serverForm ? !shallowFormEq(form, serverForm) : false), [form, serverForm]);
  const restoreDraft = () => {
    if (!detail) return;
    const stored = readDraft(userScope, detail.assessment_id);
    if (stored) {
      setForm(stored.form);
      setStaleDraft(null);
      setDraftSaved(true);
      setEditing(true);
    }
  };
  const useServerVersion = () => {
    if (!detail || !serverForm) return;
    clearDraft(userScope, detail.assessment_id);
    setForm(serverForm);
    setStaleDraft(null);
    setDraftSaved(false);
  };

  // Auto-open edit mode + scroll to the grading panel when the list's
  // green Review icon lands here with ?mode=grade.
  useEffect(() => {
    if (!detail) return;
    if (params.get("mode") === "grade") {
      setEditing(true);
      setTimeout(() => gradingPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    }
  }, [detail?.assessment_id, params]);

  const backToList = () => navigate("/staff-portal/assessments");

  // Live derivations for the grading panel — mirror the backend formula.
  const parsedMark = form.student_mark === "" ? null : Number(form.student_mark);
  const maxScore = detail?.max_score ?? null;
  const livePercentage = useMemo(() => {
    if (parsedMark === null || Number.isNaN(parsedMark) || !maxScore || maxScore <= 0) return null;
    return Math.round((parsedMark / maxScore) * 100 * 100) / 100;
  }, [parsedMark, maxScore]);
  const livePass = useMemo(() => {
    if (livePercentage === null || detail?.pass_threshold == null) return null;
    return livePercentage >= detail.pass_threshold;
  }, [livePercentage, detail?.pass_threshold]);

  const validateScore = (val: string): string | null => {
    if (val === "") return null;
    const n = Number(val);
    if (Number.isNaN(n)) return "Score must be a number.";
    if (n < 0) return "Score cannot be negative.";
    if (maxScore != null && n > maxScore) return `Score cannot exceed the maximum score of ${maxScore}.`;
    return null;
  };
  const handleScoreChange = (val: string) => {
    setForm((f) => ({ ...f, student_mark: val }));
    setScoreError(validateScore(val));
  };

  const appendFeedbackChip = (chip: string) => {
    setForm((f) => ({ ...f, comment_1: f.comment_1 ? `${f.comment_1}\n${chip}` : chip }));
  };

  const enterEditMode = () => {
    setEditing(true);
    setTimeout(() => gradingPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 40);
  };
  // Cancel: if there are unsaved changes, confirm before discarding
  // them. A single Cancel click on a dirty form must not silently
  // destroy the teacher's work.
  const cancelEdit = () => {
    if (isDirty) {
      setDiscardOpen(true);
      return;
    }
    exitEditMode();
  };
  const exitEditMode = () => {
    setEditing(false);
    setScoreError(null);
    if (params.get("mode")) {
      params.delete("mode");
      setParams(params, { replace: true });
    }
  };
  const confirmDiscard = () => {
    if (detail && serverForm) {
      clearDraft(userScope, detail.assessment_id);
      setForm(serverForm);
      setDraftSaved(false);
    }
    setDiscardOpen(false);
    exitEditMode();
  };

  const handleSave = async () => {
    const err = validateScore(form.student_mark);
    if (err) { setScoreError(err); return; }
    try {
      const resp = await updateAssessment({
        assessmentId,
        student_mark: form.student_mark === "" ? null : Number(form.student_mark),
        comment_1: form.comment_1,
        comment_2: form.comment_2,
        comment_3: form.comment_3,
        analysis: form.analysis,
      }).unwrap();
      // Success toast fires ONLY when the backend confirms status=true.
      // A status=false response (validation, scope) or a thrown error
      // falls through to the error toast with the backend's exact copy
      // so no fake-success grade is ever displayed.
      if ((resp as any)?.status) {
        // Backend confirmed. Seed the visible detail directly from the
        // mutation response so the page renders the saved grade
        // instantly — the RTK "team" tag will still trigger a
        // background refetch, but we do NOT gate the UI on that.
        const savedDetail = (resp as any).data as Detail | undefined;
        if (savedDetail) setLastGoodDetail(savedDetail);
        // Only clear the local draft AFTER confirmed backend success.
        // A failed save (network / 403 / 422) must keep the draft so
        // the teacher can retry without retyping.
        clearDraft(userScope, assessmentId);
        setDraftSaved(false);
        setStaleDraft(null);
        setToast({ severity: "success", message: "Grade updated successfully." });
        setEditing(false);
        if (params.get("mode")) { params.delete("mode"); setParams(params, { replace: true }); }
      } else {
        setToast({ severity: "error", message: (resp as any)?.message || "We couldn't update this grade. Please try again." });
      }
    } catch (e: any) {
      const status = e?.status ?? e?.originalStatus;
      const backendMsg: string | undefined = e?.data?.message;
      let msg: string;
      if (status === 403) msg = backendMsg || "You don't have access to this assessment.";
      else if (status === 422) msg = backendMsg || "Please check the grade and feedback fields.";
      else msg = backendMsg || "We couldn't update this grade. Please try again.";
      setToast({ severity: "error", message: msg });
    }
  };

  // ── Render branches ────────────────────────────────────────────
  if (isLoading || isFetching && !detail) {
    return (
      <Box sx={{ bgcolor: PAGE_BG, minHeight: "100vh", p: { xs: 2, md: 4 } }}>
        <Button startIcon={<ArrowBackIcon />} onClick={backToList} sx={{ textTransform: "none", mb: 2, color: NAVY, fontWeight: 700 }}>Back to Assessments</Button>
        <Skeleton variant="rounded" height={140} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={90} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={280} />
      </Box>
    );
  }
  // Only show the full-page error when we've NEVER had a valid detail.
  // A background refetch error (e.g. 500 after invalidation) must not
  // blow away the already-rendered assessment.
  if (!detail) {
    const is403 = httpStatus === 403;
    const is404 = httpStatus === 404;
    const title = is403 ? "You don't have access to this assessment"
      : is404 ? "Assessment not found"
      : "We couldn't load this assessment";
    const body = is403 ? "This assessment belongs to another teacher's scope. If you believe this is a mistake, contact an administrator."
      : is404 ? "It may have been removed, or the link is out of date."
      : "Please try again in a moment.";
    return (
      <Box sx={{ bgcolor: PAGE_BG, minHeight: "100vh", p: { xs: 2, md: 4 } }}>
        <Button startIcon={<ArrowBackIcon />} onClick={backToList} sx={{ textTransform: "none", mb: 2, color: NAVY, fontWeight: 700 }}>Back to Assessments</Button>
        <Card sx={{ p: 5, textAlign: "center", borderRadius: 3, border: "1px solid #e2e8f0", maxWidth: 560, mx: "auto" }}>
          <Box sx={{ width: 48, height: 48, mx: "auto", mb: 2, borderRadius: "50%", bgcolor: "#fef2f2", color: "#b91c1c", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <InfoOutlinedIcon />
          </Box>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#0f172a", mb: 1 }}>{title}</Typography>
          <Typography sx={{ fontSize: 13.5, color: "text.secondary", mb: 2 }}>{body}</Typography>
          <Button variant="contained" onClick={backToList} sx={{ textTransform: "none", background: `linear-gradient(135deg,${NAVY_2},${NAVY})`, fontWeight: 700 }}>Back to Assessments</Button>
        </Card>
      </Box>
    );
  }

  const d = detail;
  const status = workflowStatus(d);
  const scoreDisplay = d.marked && d.score !== null && d.max_score !== null ? `${d.score} / ${d.max_score}` : "Not Graded";
  const percentageDisplay = d.marked && d.percentage !== null ? `${Math.round(d.percentage)}%` : "—";
  const passDisplay = d.pass_threshold !== null ? `${Math.round(d.pass_threshold)}%` : "—";
  const openStudent = () => d.student && navigate(`/staff-portal/students/${d.student.id}`);
  const canEdit = status.label !== "Upcoming";
  const primaryCta = d.marked ? "Edit Feedback" : "Grade Assessment";

  return (
    <Box sx={{ bgcolor: PAGE_BG, minHeight: "100vh", pb: 4 }}>
      {/* ── Header ────────────────────────────────────────────────── */}
      <Box sx={{ px: { xs: 2, md: 4 }, pt: { xs: 2, md: 3 } }}>
        <Button startIcon={<ArrowBackIcon />} onClick={backToList} sx={{ textTransform: "none", mb: 1.5, color: NAVY, fontWeight: 700 }}>Back to Assessments</Button>
        <Card sx={{
          p: { xs: 2.5, md: 3 }, borderRadius: 3, border: "1px solid #e2e8f0",
          background: `linear-gradient(135deg, #ffffff 0%, #f7fafc 60%, #eef4ff 100%)`,
          position: "relative", overflow: "hidden",
          "&::before": { content: '""', position: "absolute", left: 0, right: 0, top: 0, height: 3, background: `linear-gradient(90deg, ${NAVY} 0%, ${NAVY_2} 100%)` },
        }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between">
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75, flexWrap: "wrap" }}>
                <Chip label={status.label} size="small" sx={{ bgcolor: status.bg, color: status.fg, border: `1px solid ${status.border}`, fontWeight: 800, fontSize: 11.5, letterSpacing: "0.02em" }} />
                {d.type && <Chip label={humanizeType(d.type)} size="small" sx={{ bgcolor: "#eef2ff", color: NAVY, fontWeight: 700, fontSize: 11.5 }} />}
                {d.subject && <Chip label={d.subject} size="small" sx={{ bgcolor: "#f1f5f9", color: "#334155", fontWeight: 700, fontSize: 11.5 }} />}
              </Stack>
              <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 800, color: NAVY, letterSpacing: "-0.01em" }}>{d.title}</Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0.75, sm: 2 }} sx={{ mt: 1.25, flexWrap: "wrap" }}>
                {d.student && (
                  <Stack direction="row" spacing={0.75} alignItems="center"
                    onClick={openStudent} role="button" tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openStudent(); } }}
                    sx={{ cursor: "pointer", color: "#334155", "&:hover .student-link": { color: NAVY_2, textDecoration: "underline" }, "&:focus-visible": { outline: `2px solid ${NAVY_2}55`, outlineOffset: 2, borderRadius: 1 } }}
                  >
                    <PersonOutlineIcon sx={{ fontSize: 16, color: "#64748b" }} />
                    <Typography className="student-link" sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>{d.student.name}</Typography>
                  </Stack>
                )}
                {d.grade && (
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: "#334155" }}>
                    <SchoolOutlinedIcon sx={{ fontSize: 16, color: "#64748b" }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Grade {d.grade}</Typography>
                  </Stack>
                )}
                {d.assessment_date && (
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: "#334155" }}>
                    <CalendarMonthOutlinedIcon sx={{ fontSize: 16, color: "#64748b" }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{fmtDate(d.assessment_date)}</Typography>
                  </Stack>
                )}
              </Stack>
            </Box>
            {canEdit && !editing && (
              <Tooltip title={d.marked ? "Edit the recorded feedback" : "Enter a mark and feedback for this assessment"}>
                <Button
                  variant="contained"
                  startIcon={d.marked ? <EditOutlinedIcon /> : <HowToRegOutlinedIcon />}
                  onClick={enterEditMode}
                  sx={{ textTransform: "none", fontWeight: 700, background: `linear-gradient(135deg, ${NAVY_2}, ${NAVY})`, "&:hover": { background: `linear-gradient(135deg, ${NAVY}, ${NAVY_2})` } }}
                >
                  {primaryCta}
                </Button>
              </Tooltip>
            )}
          </Stack>
        </Card>
      </Box>

      {/* ── KPI row ───────────────────────────────────────────────── */}
      <Box sx={{ px: { xs: 2, md: 4 }, mt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}><KpiTile label="Maximum Score" value={d.max_score !== null ? d.max_score : "—"} accent={NAVY} icon={<EmojiEventsOutlinedIcon />} bg="#eef2ff" /></Grid>
          <Grid item xs={6} md={3}><KpiTile label="Pass Mark" value={passDisplay} accent="#7c3aed" icon={<PercentOutlinedIcon />} bg="#f5f3ff" /></Grid>
          <Grid item xs={6} md={3}><KpiTile label="Score" value={scoreDisplay} accent={d.marked ? "#16a34a" : "#d97706"} icon={<GradeOutlinedIcon />} bg={d.marked ? "#f0fdf4" : "#fffbeb"} /></Grid>
          <Grid item xs={6} md={3}><KpiTile label="Result" value={resultDisplay(d)} accent="#0891b2" icon={<MenuBookOutlinedIcon />} bg="#ecfeff" /></Grid>
        </Grid>
      </Box>

      {/* ── Body: information + grading workspace ────────────────── */}
      <Box sx={{ px: { xs: 2, md: 4 }, mt: 2 }}>
        <Grid container spacing={2} alignItems="flex-start">
          {/* Left: assessment information + narrative */}
          <Grid item xs={12} md={7}>
            <Stack spacing={2}>
              <Card sx={{ p: 2.5, borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(15,23,42,0.04)" }}>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: NAVY, textTransform: "uppercase", letterSpacing: "0.05em", mb: 2 }}>Assessment Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}><LabelValue label="Title" value={d.title} /></Grid>
                  <Grid item xs={12} sm={6}><LabelValue label="Type" value={d.type ? humanizeType(d.type) : null} /></Grid>
                  <Grid item xs={12} sm={6}><LabelValue label="Subject" value={d.subject} /></Grid>
                  <Grid item xs={12} sm={6}><LabelValue label="Grade" value={d.grade} /></Grid>
                  <Grid item xs={12} sm={6}><LabelValue label="Assessment Date" value={fmtDate(d.assessment_date)} /></Grid>
                  <Grid item xs={12} sm={6}><LabelValue label="Due Date" value={d.due_date ? fmtDate(d.due_date) : null} /></Grid>
                  <Grid item xs={12} sm={6}><LabelValue label="Maximum Score" value={d.max_score !== null ? d.max_score : null} /></Grid>
                  <Grid item xs={12} sm={6}><LabelValue label="Pass Mark" value={d.pass_threshold !== null ? `${Math.round(d.pass_threshold)}%` : null} /></Grid>
                </Grid>
              </Card>

              <TextSection title="Learning Goal" body={d.focus} />
              <TextSection title="Instructions" body={d.instructions} />

              {/* Learning Materials — uploaded document(s) + YouTube URL(s)
                  attached when the assessment was created. Backend returns
                  them as `learning_materials` (empty array when nothing
                  attached). Rendered in both view and grade modes so the
                  teacher can reference materials while grading. */}
              {(d.learning_materials?.length ?? 0) > 0 && (
                <Card sx={{ p: 2.5, borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(15,23,42,0.04)" }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: NAVY, textTransform: "uppercase", letterSpacing: "0.05em", mb: 1.25 }}>
                    Learning Materials
                  </Typography>
                  <Stack spacing={1}>
                    {d.learning_materials!.map((m) => {
                      if (m.kind === "file") {
                        const disabled = !m.url;
                        return (
                          <Box
                            key={`file-${m.id}`}
                            sx={{
                              display: "flex", alignItems: "center", gap: 1.5,
                              p: 1.25, borderRadius: 2, bgcolor: "#f8fafc",
                              border: "1px solid #e2e8f0",
                            }}
                          >
                            <Box sx={{
                              width: 40, height: 40, borderRadius: 1.5,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              bgcolor: "#fff", border: "1px solid #e2e8f0", flexShrink: 0,
                            }}>
                              {fileIcon(m.extension, m.mime_type)}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Tooltip title={m.name} arrow>
                                <Typography sx={{
                                  fontSize: 13.5, fontWeight: 700, color: NAVY,
                                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                }}>
                                  {m.name}
                                </Typography>
                              </Tooltip>
                              <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>
                                {fileKindLabel(m.extension, m.mime_type)}
                                {humanFileSize(m.file_size) ? ` · ${humanFileSize(m.file_size)}` : ""}
                              </Typography>
                            </Box>
                            <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={disabled}
                                startIcon={<OpenInNewOutlinedIcon sx={{ fontSize: 16 }} />}
                                onClick={() => window.open(m.url, "_blank", "noopener,noreferrer")}
                                sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
                              >
                                View
                              </Button>
                              <Button
                                size="small"
                                variant="contained"
                                disabled={disabled}
                                startIcon={<DownloadOutlinedIcon sx={{ fontSize: 16 }} />}
                                component="a"
                                href={m.url || undefined}
                                download={m.name}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                  textTransform: "none", fontWeight: 700, borderRadius: 2,
                                  bgcolor: NAVY, boxShadow: "none",
                                  "&:hover": { bgcolor: NAVY_2 },
                                }}
                              >
                                Download
                              </Button>
                            </Stack>
                          </Box>
                        );
                      }
                      // YouTube row
                      const vid = m.youtube_video_id;
                      const thumb = vid ? `https://img.youtube.com/vi/${vid}/mqdefault.jpg` : null;
                      return (
                        <Box
                          key={`yt-${m.id}`}
                          sx={{
                            display: "flex", alignItems: "center", gap: 1.5,
                            p: 1.25, borderRadius: 2, bgcolor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          <Box sx={{
                            width: 72, height: 40, borderRadius: 1.5, position: "relative",
                            bgcolor: "#000", display: "flex", alignItems: "center", justifyContent: "center",
                            overflow: "hidden", flexShrink: 0,
                          }}>
                            {thumb ? (
                              // eslint-disable-next-line jsx-a11y/alt-text
                              <img
                                src={thumb}
                                alt=""
                                style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.85 }}
                              />
                            ) : null}
                            <PlayCircleOutlineIcon sx={{
                              position: "absolute", color: "#fff", fontSize: 22,
                              filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.5))",
                            }} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: NAVY }}>
                              YouTube Video
                            </Typography>
                            <Tooltip title={m.url} arrow>
                              <Typography sx={{
                                fontSize: 11.5, color: "#64748b",
                                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                              }}>
                                {m.url}
                              </Typography>
                            </Tooltip>
                          </Box>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<PlayCircleOutlineIcon sx={{ fontSize: 16 }} />}
                            onClick={() => window.open(m.url, "_blank", "noopener,noreferrer")}
                            sx={{
                              textTransform: "none", fontWeight: 700, borderRadius: 2,
                              bgcolor: "#dc2626", boxShadow: "none", flexShrink: 0,
                              "&:hover": { bgcolor: "#b91c1c" },
                            }}
                          >
                            Watch Video
                          </Button>
                        </Box>
                      );
                    })}
                  </Stack>
                </Card>
              )}

              {(d.graded_at || d.graded_by_name || d.updated_at) && (
                <Card sx={{ p: 2.5, borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(15,23,42,0.04)" }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <HistoryOutlinedIcon sx={{ fontSize: 18, color: NAVY }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: NAVY, textTransform: "uppercase", letterSpacing: "0.05em" }}>Review History</Typography>
                  </Stack>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}><LabelValue label="Graded By" value={d.graded_by_name} /></Grid>
                    <Grid item xs={12} sm={4}><LabelValue label="Graded" value={d.graded_at ? fmtDateTime(d.graded_at) : null} /></Grid>
                    <Grid item xs={12} sm={4}><LabelValue label="Last Updated" value={d.updated_at ? fmtDateTime(d.updated_at) : null} /></Grid>
                  </Grid>
                </Card>
              )}
            </Stack>
          </Grid>

          {/* Right: grading + feedback panel (sticky on desktop) */}
          <Grid item xs={12} md={5}>
            <Box ref={gradingPanelRef} sx={{ position: { md: "sticky" }, top: { md: 16 } }}>
              <Card sx={{ p: 2.5, borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 2px 10px rgba(15,23,42,0.06)" }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: "#eef2ff", color: NAVY, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {editing ? <EditOutlinedIcon fontSize="small" /> : <CheckCircleOutlineIcon fontSize="small" />}
                    </Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: NAVY, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      Grade &amp; Feedback
                    </Typography>
                    {draftSaved && isDirty && (
                      <Tooltip title="Your unsaved grading is preserved on this device until you Save Grade or explicitly discard it.">
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: 0.5, color: "#0891b2" }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#0891b2" }} />
                          <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.03em" }}>Draft saved</Typography>
                        </Stack>
                      </Tooltip>
                    )}
                  </Stack>
                  {!editing && canEdit && (
                    <Button size="small" onClick={enterEditMode} startIcon={<EditOutlinedIcon />} sx={{ textTransform: "none", fontWeight: 700, color: NAVY_2 }}>
                      {d.marked ? "Edit" : "Grade"}
                    </Button>
                  )}
                </Stack>
                {staleDraft && (
                  <Alert
                    severity="warning"
                    sx={{ mb: 1.5, fontSize: 12.5, alignItems: "center" }}
                    action={
                      <Stack direction="row" spacing={0.5}>
                        <Button size="small" onClick={useServerVersion} sx={{ textTransform: "none", fontWeight: 700 }}>Use Saved Version</Button>
                        <Button size="small" onClick={restoreDraft} sx={{ textTransform: "none", fontWeight: 700 }}>Restore My Draft</Button>
                      </Stack>
                    }
                  >
                    A newer saved version of this assessment exists.
                  </Alert>
                )}

                {/* READ MODE */}
                {!editing && (
                  <Stack spacing={2}>
                    <Box>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748b", mb: 0.5 }}>Score</Typography>
                      <Typography sx={{ fontSize: 28, fontWeight: 800, color: d.marked ? "#16a34a" : "#94a3b8", lineHeight: 1.1 }}>{scoreDisplay}</Typography>
                      {d.marked && d.percentage !== null && (
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#334155", mt: 0.5 }}>
                          {percentageDisplay}
                          {d.pass_threshold !== null && (
                            <> · <Box component="span" sx={{ color: d.pass_fail ? "#166534" : "#b91c1c" }}>{d.pass_fail ? "Passed" : "Below Pass Mark"}</Box></>
                          )}
                        </Typography>
                      )}
                    </Box>
                    <Divider sx={{ borderColor: "#f1f5f9" }} />
                    <LabelValue label="Overall Feedback" value={d.comment_1} />
                    <LabelValue label="Strengths" value={d.comment_2} />
                    <LabelValue label="Next Steps" value={d.comment_3} />
                    <LabelValue label="Teacher Analysis" value={d.analysis} />
                    {!canEdit && (
                      <Alert severity="info" sx={{ fontSize: 12.5 }}>Grading opens once this assessment's date has arrived.</Alert>
                    )}
                  </Stack>
                )}

                {/* EDIT MODE */}
                {editing && (
                  <Stack spacing={2}>
                    <Box>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748b", mb: 0.5 }}>Score</Typography>
                      <TextField
                        size="small"
                        type="number"
                        value={form.student_mark}
                        onChange={(e) => handleScoreChange(e.target.value)}
                        placeholder="0"
                        inputProps={{ min: 0, max: maxScore ?? undefined, step: 1 }}
                        error={Boolean(scoreError)}
                        helperText={scoreError || (maxScore ? `Out of ${maxScore}` : " ")}
                        InputProps={{ endAdornment: maxScore ? <InputAdornment position="end">/ {maxScore}</InputAdornment> : undefined }}
                        sx={{ width: 200 }}
                      />
                      {livePercentage !== null && (
                        <Box sx={{ mt: 1, p: 1.25, borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                          <Typography sx={{ fontSize: 12, color: "#64748b" }}>Live preview</Typography>
                          <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#0f172a" }}>
                            {Math.round(livePercentage)}%
                            {d.pass_threshold !== null && (
                              <> · <Box component="span" sx={{ color: livePass ? "#166534" : "#b91c1c" }}>{livePass ? "Passed" : "Below Pass Mark"}</Box></>
                            )}
                          </Typography>
                          {d.pass_threshold !== null && (
                            <Typography sx={{ fontSize: 11, color: "#64748b" }}>Pass threshold: {Math.round(d.pass_threshold)}%</Typography>
                          )}
                        </Box>
                      )}
                    </Box>

                    <Divider sx={{ borderColor: "#f1f5f9" }} />

                    <Box>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748b", mb: 0.5 }}>Overall Feedback</Typography>
                      <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: "wrap", gap: 0.5 }}>
                        {FEEDBACK_CHIPS.map((c) => (
                          <Chip key={c} label={`+ ${c}`} size="small" onClick={() => appendFeedbackChip(c)} sx={{ bgcolor: "#eef2ff", color: NAVY, fontWeight: 600, fontSize: 11, cursor: "pointer", "&:hover": { bgcolor: "#e0e7ff" } }} />
                        ))}
                      </Stack>
                      <TextField fullWidth multiline minRows={3} placeholder="What did the student do overall?" value={form.comment_1} onChange={(e) => setForm({ ...form, comment_1: e.target.value })} />
                    </Box>

                    <Box>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748b", mb: 0.5 }}>Strengths</Typography>
                      <TextField fullWidth multiline minRows={2} placeholder="What did the student do well?" value={form.comment_2} onChange={(e) => setForm({ ...form, comment_2: e.target.value })} />
                    </Box>

                    <Box>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748b", mb: 0.5 }}>Next Steps</Typography>
                      <TextField fullWidth multiline minRows={2} placeholder="What should the student focus on next?" value={form.comment_3} onChange={(e) => setForm({ ...form, comment_3: e.target.value })} />
                    </Box>

                    <Box>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748b", mb: 0.5 }}>Teacher Analysis</Typography>
                      <TextField fullWidth multiline minRows={2} placeholder="Internal notes on this assessment." value={form.analysis} onChange={(e) => setForm({ ...form, analysis: e.target.value })} />
                    </Box>

                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button onClick={cancelEdit} disabled={isSaving} sx={{ textTransform: "none", fontWeight: 700, color: "#475569" }}>Cancel</Button>
                      <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={isSaving || Boolean(scoreError)}
                        startIcon={<SaveOutlinedIcon />}
                        sx={{ textTransform: "none", fontWeight: 700, background: `linear-gradient(135deg, ${NAVY_2}, ${NAVY})`, "&:hover": { background: `linear-gradient(135deg, ${NAVY}, ${NAVY_2})` } }}
                      >
                        {isSaving ? "Saving…" : (d.marked ? "Save Feedback" : "Save Grade")}
                      </Button>
                    </Stack>
                  </Stack>
                )}
              </Card>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Top-right toast, underneath the app header. Positioning is
          local to this page so Admin and other Staff Portal pages keep
          the shared notifier's default anchor unchanged. Fixed offsets:
          desktop ~76px (below header), mobile pinned near the top with
          side gutters. */}
      <Dialog open={discardOpen} onClose={() => setDiscardOpen(false)}>
        <DialogTitle sx={{ fontWeight: 800, color: NAVY }}>Discard unsaved grading changes?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 13.5 }}>
            You have edits that haven't been saved. Discarding will remove them from this device.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardOpen(false)} sx={{ textTransform: "none", fontWeight: 700 }}>Keep Editing</Button>
          <Button onClick={confirmDiscard} color="error" variant="contained" sx={{ textTransform: "none", fontWeight: 700 }}>Discard Changes</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={3200}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          top: { xs: "68px !important", md: "76px !important" },
          right: { xs: "12px !important", md: "24px !important" },
          left: { xs: "12px !important", md: "auto !important" },
          width: { xs: "calc(100vw - 24px)", md: "auto" },
          maxWidth: 420,
        }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            variant="standard"
            onClose={() => setToast(null)}
            sx={{
              width: "100%",
              borderRadius: 2,
              boxShadow: "0 6px 20px rgba(15,23,42,0.14)",
              fontWeight: 600,
              alignItems: "center",
              ...(toast.severity === "error" ? {
                bgcolor: "#fef2f2",
                color: "#7f1d1d",
                border: "1px solid #fecaca",
                "& .MuiAlert-icon": { color: "#dc2626" },
              } : {
                bgcolor: "#f0fdf4",
                color: "#14532d",
                border: "1px solid #bbf7d0",
                "& .MuiAlert-icon": { color: "#16a34a" },
              }),
            }}
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default AssessmentDetail;
